import axios from "axios";
import type {
  CalendarJurisdiction,
  CalendarMutationResult,
  CalendarOverrideCreate,
  CalendarOverridePatch,
  CalendarSettingsUpdate,
  CalendarSubdivision,
  CalendarTimezone,
  OrganizationCalendar,
} from "model/organizationCalendarModel";
import errorInterceptor from "./errorInterceptor";
import jwtInterceptor from "./jwtInterceptor";

const calendarApi = axios.create({
  baseURL: `${process.env.REACT_APP_STATS_BACKEND_BASE_URI}/calendar`,
  headers: { "Content-Type": "application/json" },
});

calendarApi.interceptors.request.use(jwtInterceptor);
calendarApi.interceptors.response.use(undefined, errorInterceptor);

export const getCalendarTimezones = async (): Promise<CalendarTimezone[]> =>
  (await calendarApi.get<{ data: CalendarTimezone[] }>("/timezones")).data.data;

export const getCalendarJurisdictions = async (): Promise<CalendarJurisdiction[]> =>
  (await calendarApi.get<{ data: CalendarJurisdiction[] }>("/jurisdictions")).data.data;

export const getCalendarSubdivisions = async (
  countryCode: string,
): Promise<CalendarSubdivision[]> =>
  (
    await calendarApi.get<{ data: CalendarSubdivision[] }>(
      `/jurisdictions/${countryCode}/subdivisions`,
    )
  ).data.data;

export const getOrganizationCalendar = async (
  id: number,
  from: string,
  to: string,
): Promise<OrganizationCalendar> =>
  (
    await calendarApi.get<OrganizationCalendar>(`/organizations/${id}`, {
      params: { from, to },
    })
  ).data;

export const putOrganizationCalendar = async (
  id: number,
  body: CalendarSettingsUpdate,
): Promise<CalendarMutationResult> =>
  (await calendarApi.put<CalendarMutationResult>(`/organizations/${id}`, body)).data;

export const createCalendarOverride = async (
  id: number,
  body: CalendarOverrideCreate,
): Promise<CalendarMutationResult> =>
  (
    await calendarApi.post<CalendarMutationResult>(
      `/organizations/${id}/overrides`,
      body,
    )
  ).data;

export const updateCalendarOverride = async (
  id: number,
  overrideId: number,
  body: CalendarOverridePatch,
): Promise<CalendarMutationResult> =>
  (
    await calendarApi.patch<CalendarMutationResult>(
      `/organizations/${id}/overrides/${overrideId}`,
      body,
    )
  ).data;

export const deleteCalendarOverride = async (
  id: number,
  overrideId: number,
  expectedCalendarRevision: number,
): Promise<CalendarMutationResult> =>
  (
    await calendarApi.delete<CalendarMutationResult>(
      `/organizations/${id}/overrides/${overrideId}`,
      { params: { expectedCalendarRevision } },
    )
  ).data;

export default calendarApi;
